import Keycloak, { type KeycloakInstance } from "keycloak-js";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type AuthMode = "anonymous" | "demo" | "keycloak";

type AuthState = {
  ready: boolean;
  authenticated: boolean;
  mode: AuthMode;
  tenantId: string | null;
  roles: string[];
  token: string | null;
  email?: string;
  name?: string;
  error?: string;
  login: () => void;
  register: () => void;
  startDemo: () => void;
  logout: () => void;
};

const Ctx = createContext<AuthState | null>(null);

const DEMO_SESSION_KEY = "kpi.enterprise.demo";
const TOKEN_SESSION_KEY = "kpi.enterprise.keycloak.tokens";
const isDemoModeBuild = import.meta.env.VITE_DEMO_MODE === "true";

const kcConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL ?? "https://auth.ben-e-fit.ai",
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? "kpi-mining",
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "kpi-cockpit",
};

let kc: KeycloakInstance | null = null;
let kcInitPromise: Promise<boolean> | null = null;

function createKeycloak() {
  if (!kc) kc = new Keycloak(kcConfig);
  return kc;
}

function appRedirect(path = "/app/onboarding") {
  return `${window.location.origin}${path}`;
}

function hasStoredDemo() {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem(DEMO_SESSION_KEY) === "true";
}

function hasKeycloakCallback() {
  if (typeof window === "undefined") return false;
  const locationText = `${window.location.search} ${window.location.hash}`;
  return locationText.includes("code=") || locationText.includes("session_state=");
}

function storeDemo(enabled: boolean) {
  if (typeof window === "undefined") return;
  if (enabled) window.sessionStorage.setItem(DEMO_SESSION_KEY, "true");
  else window.sessionStorage.removeItem(DEMO_SESSION_KEY);
}

function readStoredTokens() {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(TOKEN_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { token?: string; refreshToken?: string; idToken?: string; timeSkew?: number };
  } catch {
    window.sessionStorage.removeItem(TOKEN_SESSION_KEY);
    return null;
  }
}

function storeTokens(instance: KeycloakInstance) {
  if (typeof window === "undefined") return;
  if (!instance.token || !instance.refreshToken) return;
  window.sessionStorage.setItem(TOKEN_SESSION_KEY, JSON.stringify({
    token: instance.token,
    refreshToken: instance.refreshToken,
    idToken: instance.idToken,
    timeSkew: instance.timeSkew,
  }));
}

function clearTokens() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(TOKEN_SESSION_KEY);
}

function extractRoles(claims: Record<string, unknown>): string[] {
  const realmRoles = (claims.realm_access as { roles?: string[] } | undefined)?.roles ?? [];
  const resourceAccess = claims.resource_access as Record<string, { roles?: string[] }> | undefined;
  const clientRoles = resourceAccess?.[kcConfig.clientId]?.roles ?? [];
  return Array.from(new Set([...realmRoles, ...clientRoles]));
}

function keycloakState(instance: KeycloakInstance, demoTenant: string): AuthState {
  const claims = (instance.tokenParsed ?? {}) as Record<string, unknown>;
  const email = claims.email as string | undefined;
  const preferred = claims.preferred_username as string | undefined;
  const name = (claims.name as string | undefined) ?? preferred ?? email;
  return {
    ready: true,
    authenticated: true,
    mode: "keycloak",
    tenantId: (claims.tenant_id as string | undefined) ?? null,
    roles: extractRoles(claims),
    token: instance.token ?? null,
    email,
    name,
    login: () => triggerKeycloakLogin(),
    register: () => triggerKeycloakRegister(),
    startDemo: () => startDemoMode(demoTenant),
    logout: () => {
      storeDemo(false);
      clearTokens();
      instance.logout({ redirectUri: window.location.origin });
    },
  };
}

function demoState(demoTenant: string): AuthState {
  return {
    ready: true,
    authenticated: true,
    mode: "demo",
    tenantId: demoTenant,
    roles: ["cfo", "tenant_admin"],
    token: null,
    email: undefined,
    name: "cfo",
    login: () => triggerKeycloakLogin(),
    register: () => triggerKeycloakRegister(),
    startDemo: () => startDemoMode(demoTenant),
    logout: () => {
      storeDemo(false);
      clearTokens();
      window.location.assign("/");
    },
  };
}

function anonymousState(demoTenant: string, ready = true, error?: string): AuthState {
  return {
    ready,
    authenticated: false,
    mode: "anonymous",
    tenantId: null,
    roles: [],
    token: null,
    error,
    login: () => triggerKeycloakLogin(),
    register: () => triggerKeycloakRegister(),
    startDemo: () => startDemoMode(demoTenant),
    logout: () => {
      storeDemo(false);
      clearTokens();
      window.location.assign("/");
    },
  };
}

function initKeycloakPassive() {
  const instance = createKeycloak();
  if (!kcInitPromise) {
    kcInitPromise = instance.init({
      checkLoginIframe: false,
      pkceMethod: "S256",
    });
  }
  return kcInitPromise;
}

function initKeycloakFromStoredTokens() {
  const instance = createKeycloak();
  const stored = readStoredTokens();
  if (!stored?.token || !stored.refreshToken) return Promise.resolve(false);
  if (!kcInitPromise) {
    kcInitPromise = instance.init({
      token: stored.token,
      refreshToken: stored.refreshToken,
      idToken: stored.idToken,
      timeSkew: stored.timeSkew,
      checkLoginIframe: false,
      pkceMethod: "S256",
    });
  }
  return kcInitPromise;
}

function triggerKeycloakLogin() {
  storeDemo(false);
  clearTokens();
  initKeycloakPassive()
    .catch(() => false)
    .finally(() => {
      createKeycloak().login({ redirectUri: appRedirect("/app/onboarding") });
    });
}

function triggerKeycloakRegister() {
  storeDemo(false);
  clearTokens();
  initKeycloakPassive()
    .catch(() => false)
    .finally(() => {
      createKeycloak().register({ redirectUri: appRedirect("/app/onboarding") });
    });
}

function startDemoMode(_demoTenant: string) {
  storeDemo(true);
  window.location.assign("/app/onboarding");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const demoTenant = import.meta.env.VITE_DEMO_TENANT ?? "tnt_demo";
  const [state, setState] = useState<AuthState>(() => {
    if (isDemoModeBuild || hasStoredDemo()) return demoState(demoTenant);
    return anonymousState(demoTenant, !(hasKeycloakCallback() || readStoredTokens()));
  });

  useEffect(() => {
    if (isDemoModeBuild || hasStoredDemo()) {
      setState(demoState(demoTenant));
      return;
    }

    const shouldParseCallback = hasKeycloakCallback();
    const shouldRestoreStoredTokens = !shouldParseCallback && Boolean(readStoredTokens());

    if (!shouldParseCallback && !shouldRestoreStoredTokens) {
      setState(anonymousState(demoTenant));
      return;
    }

    let cancelled = false;
    setState(anonymousState(demoTenant, false));

    const init = shouldParseCallback ? initKeycloakPassive : initKeycloakFromStoredTokens;

    init()
      .then((authenticated) => {
        if (cancelled) return;
        if (authenticated && kc?.token) {
          storeTokens(kc);
          setState(keycloakState(kc, demoTenant));
          return;
        }
        clearTokens();
        setState(anonymousState(demoTenant));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[auth] Keycloak check-sso failed:", err);
        clearTokens();
        setState(anonymousState(demoTenant, true, "keycloak_check_sso_failed"));
      });

    const refresh = window.setInterval(() => {
      if (!kc?.authenticated) return;
      kc.updateToken(60)
        .then((refreshed) => {
          if (refreshed && kc) {
            storeTokens(kc);
            setState(keycloakState(kc, demoTenant));
          }
        })
        .catch(() => {
          storeDemo(false);
          clearTokens();
          kc?.login({ redirectUri: appRedirect("/app/onboarding") });
        });
    }, 30_000);

    return () => {
      cancelled = true;
      window.clearInterval(refresh);
    };
  }, [demoTenant]);

  const value = useMemo(() => state, [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, authenticated, tenantId, mode, error, login, register, startDemo, logout } = useAuth();
  if (!ready) return <div className="bootstrapping">Authentifizierung wird geprüft...</div>;
  if (!authenticated) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-panel">
          <p className="hero-eyebrow">KPI Enterprise Mining</p>
          <h1>Anmeldung erforderlich</h1>
          <p>
            Echte Mandanten arbeiten nur nach Login mit ihrem Tenant-Kontext. Die Sandbox bleibt getrennt
            und ist sichtbar als Demo markiert.
          </p>
          {error && <p className="auth-warning">Keycloak-Check fehlgeschlagen: {error}</p>}
          <div className="auth-gate-actions">
            <button className="btn primary big" onClick={login}>Sign in</button>
            <button className="btn ghost big" onClick={register}>Register</button>
            <button className="btn ghost big" onClick={startDemo}>Sandbox-Demo</button>
          </div>
          <a className="auth-back" href="/">Zur Produktseite</a>
        </div>
      </div>
    );
  }
  if (!tenantId)
    return (
      <div className="auth-gate">
        <div className="auth-gate-panel error">
          <p className="hero-eyebrow">JWT unvollständig</p>
          <h1>Tenant-Zuordnung fehlt</h1>
          <p>
            Der Keycloak-Login ist gültig, aber das Token enthält keinen <code>tenant_id</code>-Claim.
            Ohne Tenant-ID darf das Cockpit keine Daten laden.
          </p>
          <div className="auth-gate-actions">
            <button className="btn ghost big" onClick={logout}>Abmelden</button>
            {mode !== "demo" && <button className="btn ghost big" onClick={startDemo}>Sandbox-Demo</button>}
          </div>
        </div>
      </div>
    );
  return <>{children}</>;
}
